'use client'

import * as React from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { typographyVariants } from '../lib'
import { Button } from './button'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from './input-group'

const Combobox = ComboboxPrimitive.Root

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot='combobox-value' {...props} />
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot='combobox-trigger'
      className={cn("ui:[&_svg:not([class*='size-'])]:size-4", className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className='ui:text-grey-100 ui:size-4 ui:pointer-events-none' />
    </ComboboxPrimitive.Trigger>
  )
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot='combobox-clear'
      className={cn(className)}
      {...props}
      render={
        <InputGroupButton variant='ghost' size='icon-xs'>
          <XIcon className='ui:pointer-events-none' />
        </InputGroupButton>
      }
    />
  )
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  inputSize = 'default',
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean
  showClear?: boolean
  inputSize?: 'sm' | 'default'
}) {
  return (
    <InputGroup size={inputSize} className={cn('ui:w-auto', className)}>
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
      <InputGroupAddon align='inline-end'>
        {showTrigger && (
          <ComboboxTrigger
            render={
              <InputGroupButton
                size='icon-xs'
                variant='ghost'
                data-slot='input-group-button'
                className='ui:group-has-data-[slot=combobox-clear]/input-group:hidden ui:data-pressed:bg-transparent'
                disabled={disabled}
              />
            }
          />
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  )
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className='ui:isolate ui:z-50'
      >
        <ComboboxPrimitive.Popup
          data-slot='combobox-content'
          data-chips={!!anchor}
          className={cn(
            'ui:bg-neutral-white ui:text-neutral-black ui:data-open:animate-in ui:data-closed:animate-out ui:data-closed:fade-out-0 ui:data-open:fade-in-0 ui:data-closed:zoom-out-95 ui:data-open:zoom-in-95 ui:data-[side=bottom]:slide-in-from-top-2 ui:data-[side=left]:slide-in-from-right-2 ui:data-[side=right]:slide-in-from-left-2 ui:data-[side=top]:slide-in-from-bottom-2 ui:ring-grey-20 ui:*:data-[slot=input-group]:bg-neutral-white/30 ui:*:data-[slot=input-group]:border-neutral-white/30 ui:max-h-72 ui:min-w-36 ui:overflow-hidden ui:rounded-lg ui:shadow-md ui:ring-1 ui:duration-100 ui:*:data-[slot=input-group]:m-1 ui:*:data-[slot=input-group]:mb-0 ui:*:data-[slot=input-group]:h-12 ui:*:data-[slot=input-group]:shadow-none ui:data-[side=inline-start]:slide-in-from-right-2 ui:data-[side=inline-end]:slide-in-from-left-2 ui:group/combobox-content ui:relative ui:w-(--anchor-width) ui:max-w-(--available-width) ui:origin-(--transform-origin) ui:data-[chips=true]:min-w-(--anchor-width)',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot='combobox-list'
      className={cn(
        'no-scrollbar ui:max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] ui:scroll-py-1 ui:overflow-y-auto ui:p-1 ui:data-empty:p-0 ui:overscroll-contain',
        className,
      )}
      {...props}
    />
  )
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot='combobox-item'
      className={cn(
        "ui:data-highlighted:bg-blue-10 ui:data-highlighted:text-neutral-black ui:not-data-[variant=destructive]:data-highlighted:**:text-neutral-black ui:gap-2 ui:rounded-md ui:py-1 ui:pr-8 ui:pl-1.5 ui:text-sm ui:[&_svg:not([class*='size-'])]:size-4 ui:relative ui:flex ui:w-full ui:cursor-default ui:items-center ui:outline-hidden ui:select-none ui:data-disabled:pointer-events-none ui:data-disabled:opacity-50 ui:[&_svg]:pointer-events-none ui:[&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className='ui:pointer-events-none ui:absolute ui:right-2 ui:flex ui:size-4 ui:items-center ui:justify-center'>
            <CheckIcon className='ui:pointer-events-none ui:text-blue-100' />
          </span>
        }
      />
    </ComboboxPrimitive.Item>
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot='combobox-group' className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot='combobox-label'
      className={cn('ui:text-grey-100 ui:px-2 ui:py-1.5 ui:text-xs', className)}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot='combobox-collection' {...props} />
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot='combobox-empty'
      className={cn(
        'ui:text-grey-100 ui:hidden ui:w-full ui:justify-center ui:py-2 ui:text-center ui:text-sm ui:group-data-empty/combobox-content:flex',
        className,
      )}
      {...props}
    />
  )
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot='combobox-separator'
      className={cn('ui:bg-grey-20 ui:-mx-1 ui:my-1 ui:h-px', className)}
      {...props}
    />
  )
}

function ComboboxChips({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) {
  return (
    <ComboboxPrimitive.Chips
      data-slot='combobox-chips'
      className={cn(
        'ui:border-outline ui:focus-within:border-blue-100 ui:focus-within:ring-blue-100/50 ui:has-aria-invalid:ring-error-container ui:has-aria-invalid:border-error ui:flex ui:min-h-12 ui:flex-wrap ui:items-center ui:gap-1 ui:rounded-lg ui:border ui:bg-transparent ui:bg-clip-padding ui:px-2.5 ui:py-1 ui:text-sm ui:transition-colors ui:focus-within:ring-[3px] ui:has-aria-invalid:ring-[3px] ui:has-data-[slot=combobox-chip]:px-1',
        className,
      )}
      {...props}
    />
  )
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  removeIconClassName,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean
  removeIconClassName?: string
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot='combobox-chip'
      className={cn(
        'ui:bg-grey-20 ui:text-neutral-black ui:flex ui:h-[calc(--spacing(7))] ui:w-fit ui:items-center ui:justify-center ui:gap-1 ui:rounded-sm ui:px-2 ui:whitespace-nowrap ui:has-data-[slot=combobox-chip-remove]:pr-0 ui:has-disabled:pointer-events-none ui:has-disabled:cursor-not-allowed ui:has-disabled:opacity-50',
        typographyVariants({ variant: 'label-lg' }),
        className,
      )}
      {...props}
    >
      {children}

      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className='ui:-ml-0.5 ui:opacity-50 ui:hover:opacity-100'
          data-slot='combobox-chip-remove'
          render={
            <Button variant='ghost' size='icon' className={cn('ui:size-4', removeIconClassName)}>
              <XIcon className='ui:pointer-events-none ui:size-4' />
            </Button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  )
}

function ComboboxChipsInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot='combobox-chip-input'
      className={cn(
        'ui:min-w-16 ui:flex-1 ui:outline-none ui:placeholder:text-grey-100 ui:disabled:pointer-events-none ui:disabled:cursor-not-allowed ui:disabled:opacity-50',
        className,
      )}
      {...props}
    />
  )
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
}
